import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleList } from '../../components/ToggleList.js';
import {
  MCP_CONNECTOR_CATALOG,
  localExtensionsForConnectorIds,
  withoutRetiredConnectorIds
} from '../../catalogs.js';

const CONNECTOR_LABELS = new Map(MCP_CONNECTOR_CATALOG.map((connector) => [connector.id, connector.label]));

/** Tenant extension app bundle activation. The wire contract is still connector ids
 *  (`active_mcp_connectors`), but the operator picks **extension app bundles** (e.g.
 *  the Windows-only 한컴/HWP app), not raw MCP connectors — the connector is the
 *  extension's execution channel, shown only as secondary meta. The connector
 *  twin of ModelAssignmentTab, but an empty selection is valid (turn every
 *  extension off), so the save button stays enabled at zero selected. */
export function ToolActivationTab({
  tenantId,
  assignable,
  selected,
  setSelected,
  busy,
  onSave,
  readOnly = false
}: {
  tenantId: string;
  assignable: string[];
  selected: string[];
  setSelected: (update: (current: string[]) => string[]) => void;
  busy: boolean;
  onSave: () => Promise<void>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  // Always offer anything already active even if it later leaves the assignable
  // set, so an operator can still see (and turn off) a stranded connector. A
  // retired connector (the parked Excel app) is the exception: it never comes
  // back as a raw row, even from an older server's response.
  const connectorOptions = useMemo(
    () => withoutRetiredConnectorIds([...new Set([...assignable, ...selected])]),
    [assignable, selected]
  );
  // Present the connector set as extension app bundles; keep any connector that does
  // not belong to a catalog extension as a raw fallback row so nothing strands.
  const extensions = useMemo(() => localExtensionsForConnectorIds(connectorOptions), [connectorOptions]);
  const mappedConnectorIds = useMemo(
    () => new Set(extensions.flatMap((extension) => extension.connectorIds)),
    [extensions]
  );
  const unmappedConnectorIds = useMemo(
    () => connectorOptions.filter((id) => !mappedConnectorIds.has(id)),
    [connectorOptions, mappedConnectorIds]
  );

  function toggleConnectors(connectorIds: string[], checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      for (const id of connectorIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return [...next];
    });
  }

  const toggleOptions = useMemo(
    () => [
      ...extensions.map((extension) => ({
        key: extension.id,
        label: extension.label,
        checked: extension.connectorIds.every((id) => selected.includes(id)),
        connectorIds: extension.connectorIds,
        meta: (
          <small>
            {extension.platforms.map((platform) => t(`toolActivation.platform.${platform}`)).join(' · ')} ·
            {t('toolActivation.localExtension')} ·{' '}
            {t('toolActivation.mcpChannel', {
              connectors: extension.connectorIds.join(', ')
            })}
          </small>
        )
      })),
      ...unmappedConnectorIds.map((connectorId) => ({
        key: connectorId,
        label: CONNECTOR_LABELS.get(connectorId) ?? connectorId,
        checked: selected.includes(connectorId),
        connectorIds: [connectorId],
        meta: <small>{connectorId}</small>
      }))
    ],
    [extensions, selected, t, unmappedConnectorIds]
  );

  return (
    <ToggleList
      title={t('toolActivation.toolActivation')}
      help={t('toolActivation.hint')}
      empty={t('toolActivation.empty')}
      options={toggleOptions}
      allowEmpty
      busy={busy}
      disabled={!tenantId.trim()}
      readOnly={readOnly}
      saveLabel={t('toolActivation.save')}
      onToggle={(key, checked) =>
        toggleConnectors(toggleOptions.find((option) => option.key === key)?.connectorIds ?? [key], checked)
      }
      onSave={onSave}
    />
  );
}
